import { Box, Grid, Heading, Icon, Stack, VStack } from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import { iBriefcase, iUser } from '@hover/icons';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router';
import type { InferType } from 'yup';

import { OnboardingFormFooter } from 'src/modules/onboarding/components/OnboardingFormFooter';
import { useOnboardingError } from 'src/modules/onboarding/hooks/useOnboardingError';
import { makePersonaValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { CardRadioGroup } from 'src/shared/components/CardRadio';
import { Form as SharedForm } from 'src/shared/components/forms';
import {
  OnboardingSignupTypes,
  useOnboardingCreateOrgForUserMutation,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';

export type UserPersonaFormData = InferType<
  ReturnType<typeof makePersonaValidationSchema>
>;

interface RadioOptionProps {
  icon: IconProp;
  header?: string;
}

const RadioOption = ({ icon, header }: RadioOptionProps) => (
  <VStack
    alignContent="center"
    flex={1}
    justifyContent="center"
    minWidth={700}
    width={1}
  >
    <Icon icon={icon} size="large" />
    <Heading size={300}>{header}</Heading>
  </VStack>
);

interface UsePersonaFormFieldsProps {
  onSubmit: (formData: UserPersonaFormData) => void;
}

const UsePersonaFormFields = ({ onSubmit }: UsePersonaFormFieldsProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const isMobileSize = useIsMobileSize();

  const personaTypeOptions = [
    {
      value: OnboardingSignupTypes.Construction,
      render: <RadioOption header="Work" icon={iBriefcase} />,
    },
    {
      value: OnboardingSignupTypes.Homeowner,
      render: <RadioOption header="Personal" icon={iUser} />,
    },
  ];
  const { handleSubmit } = useFormContext();

  const buttonNameMap = {
    [OnboardingSignupTypes.Construction]: 'Work',
    [OnboardingSignupTypes.Homeowner]: 'Personal',
  };

  return (
    <Stack
      direction={{ base: 'column', tablet: 'row' }}
      flex={1}
      flexWrap="wrap"
      justifyContent={{ base: 'start', tablet: 'space-around' }}
      marginTop={300}
      spacing={0}
      width={1}
    >
      <Heading size={{ base: 700, tablet: 800 }} whiteSpace="pre">
        {t('onboarding.whatAreYouUsing')}
      </Heading>
      <Box flex={{ base: 1, tablet: 0.5 }}>
        <Controller
          name="personaType"
          render={props => (
            <CardRadioGroup
              isMobile={isMobileSize}
              onChange={val => {
                buttonPressed({
                  // @ts-ignore
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  button_text: buttonNameMap[val],
                  page_or_screen_name: 'Onboarding-account-type_input',
                  feature: 'onboarding',
                });
                props.onChange(val);
                if (isMobileSize) {
                  void handleSubmit(onSubmit)();
                }
              }}
              options={personaTypeOptions}
              width={1}
            />
          )}
        />
      </Box>
    </Stack>
  );
};

export const UserPersona = () => {
  const history = useHistory();
  const { logError } = useOnboardingError();
  const validationSchema = makePersonaValidationSchema();
  const initialValues = { personaType: undefined };
  const [createOrgForUser] = useOnboardingCreateOrgForUserMutation();

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-account-type_input',
      feature: 'onboarding',
    });
  });

  const onSubmit = async (formData: UserPersonaFormData) => {
    const { personaType } = formData;
    if (!personaType) return;
    if (personaType === OnboardingSignupTypes.Construction) {
      history.push(`${Routes.SIGN_UP_ONBOARDING.path}/type`);
    } else {
      try {
        await createOrgForUser({
          variables: {
            createOrgForUserArgs: {
              signupType: OnboardingSignupTypes.Homeowner,
            },
          },
        });
      } catch (e) {
        logError(e as Error);
      } finally {
        history.push(`${Routes.SIGN_UP_ONBOARDING.path}/download`);
      }
    }
  };

  return (
    <Grid alignItems="stretch" flex={1} width={1}>
      <SharedForm<UserPersonaFormData>
        display="flex"
        height="100%"
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <UsePersonaFormFields onSubmit={onSubmit} />
        <OnboardingFormFooter />
      </SharedForm>
    </Grid>
  );
};
